import React from 'react';
import { Helmet } from 'react-helmet-async';
import { Link } from 'react-router-dom';

export default function HowItWorks() {
  return (
    <>
      <Helmet>
        <title>How It Works - Aramco Leads Pro CRM Platform</title>
        <meta name="description" content="Learn how Aramco Leads Pro CRM platform helps you manage leads, track customer relationships, and send automated notifications." />
      </Helmet>

      <div style={{ background: 'linear-gradient(135deg, #1e40af 0%, #3b82f6 100%)', color: 'white', padding: '4rem 2rem', textAlign: 'center' }}>
        <div style={{ maxWidth: '1200px', margin: '0 auto' }}>
          <h1 style={{ fontSize: '3rem', fontWeight: '800', marginBottom: '1rem' }}>How It Works</h1>
          <p style={{ fontSize: '1.25rem', opacity: 0.95 }}>Simple, powerful CRM platform in 4 easy steps</p>
        </div>
      </div>

      <div style={{ maxWidth: '1200px', margin: '0 auto', padding: '4rem 2rem' }}>
        {/* Hero Image */}
        <div style={{ marginBottom: '4rem', borderRadius: '16px', overflow: 'hidden', boxShadow: '0 20px 60px rgba(0,0,0,0.1)' }}>
          <img 
            src="https://images.unsplash.com/photo-1551434678-e076c223a692?w=1200&h=400&fit=crop" 
            alt="Team working with CRM platform" 
            style={{ width: '100%', height: '400px', objectFit: 'cover' }}
          />
        </div>

        <div style={{ background: '#dbeafe', padding: '2rem', borderRadius: '12px', marginBottom: '3rem', border: '2px solid #3b82f6' }}>
          <h2 style={{ color: '#1e40af', marginBottom: '1rem', fontSize: '1.5rem' }}>Aramco Leads Pro Platform Model</h2>
          <p style={{ color: '#1e3a8a', fontSize: '1.1rem', lineHeight: '1.8' }}>
            Aramco Leads Pro is a <strong>CRM and lead management platform</strong> that helps businesses track leads, manage customer relationships, 
            and send automated appointment reminders and notifications. Each business maintains their own customer database and controls all communications.
          </p>
        </div>

        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))', gap: '2rem', marginBottom: '4rem' }}>
          <div style={{ background: 'white', padding: '2.5rem', borderRadius: '12px', border: '2px solid #e5e7eb' }}>
            <div style={{ fontSize: '3rem', marginBottom: '1rem' }}>1️⃣</div>
            <h3 style={{ fontSize: '1.5rem', fontWeight: '700', color: '#1f2937', marginBottom: '1rem' }}>
              Import Your Leads
            </h3>
            <p style={{ color: '#6b7280', lineHeight: '1.6' }}>
              Import leads from your existing systems, spreadsheets, or capture them through web forms. All lead data is stored securely in your account.
            </p>
          </div>

          <div style={{ background: 'white', padding: '2.5rem', borderRadius: '12px', border: '2px solid #e5e7eb' }}>
            <div style={{ fontSize: '3rem', marginBottom: '1rem' }}>2️⃣</div>
            <h3 style={{ fontSize: '1.5rem', fontWeight: '700', color: '#1f2937', marginBottom: '1rem' }}>
              Track & Manage
            </h3>
            <p style={{ color: '#6b7280', lineHeight: '1.6' }}>
              Track lead status, log interactions, schedule follow-ups, and move leads through your sales pipeline. All activity is recorded automatically.
            </p>
          </div>

          <div style={{ background: 'white', padding: '2.5rem', borderRadius: '12px', border: '2px solid #e5e7eb' }}>
            <div style={{ fontSize: '3rem', marginBottom: '1rem' }}>3️⃣</div>
            <h3 style={{ fontSize: '1.5rem', fontWeight: '700', color: '#1f2937', marginBottom: '1rem' }}>
              Automate Communications
            </h3>
            <p style={{ color: '#6b7280', lineHeight: '1.6' }}>
              Set up automated appointment reminders, follow-up messages, and customer notifications. All messages require customer opt-in consent.
            </p>
          </div>

          <div style={{ background: 'white', padding: '2.5rem', borderRadius: '12px', border: '2px solid #e5e7eb' }}>
            <div style={{ fontSize: '3rem', marginBottom: '1rem' }}>4️⃣</div>
            <h3 style={{ fontSize: '1.5rem', fontWeight: '700', color: '#1f2937', marginBottom: '1rem' }}>
              Analyze & Optimize
            </h3>
            <p style={{ color: '#6b7280', lineHeight: '1.6' }}>
              View analytics on lead conversion, customer engagement, and communication effectiveness. Use insights to improve your processes.
            </p>
          </div>
        </div>

        <div style={{ background: '#f9fafb', padding: '3rem', borderRadius: '12px', marginBottom: '3rem' }}>
          <h2 style={{ fontSize: '2rem', fontWeight: '700', color: '#1f2937', marginBottom: '2rem', textAlign: 'center' }}>
            What You Can Do
          </h2>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(250px, 1fr))', gap: '1.5rem' }}>
            {[
              'Track unlimited leads and customers',
              'Send appointment reminders',
              'Automate follow-up messages',
              'Manage your sales pipeline',
              'Log customer interactions',
              'Schedule tasks and reminders',
              'Send order status updates',
              'Notify customers of account changes',
              'View analytics and reports',
              'Integrate with existing tools'
            ].map((item, idx) => (
              <div key={idx} style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                <span style={{ color: '#1e40af', fontWeight: '600' }}>✓</span>
                <span style={{ color: '#4b5563' }}>{item}</span>
              </div>
            ))}
          </div>
        </div>

        <div style={{ background: '#fee2e2', padding: '2rem', borderRadius: '12px', marginBottom: '3rem', border: '2px solid #ef4444' }}>
          <h2 style={{ color: '#991b1b', marginBottom: '1rem', fontSize: '1.5rem' }}>Compliance Requirements</h2>
          <p style={{ color: '#7f1d1d', marginBottom: '1rem', lineHeight: '1.8' }}>
            All customer communications through our platform require:
          </p>
          <ul style={{ color: '#7f1d1d', lineHeight: '2', paddingLeft: '2rem' }}>
            <li>Explicit opt-in consent from customers</li>
            <li>Clear identification of your business</li>
            <li>Easy opt-out mechanism (STOP keyword)</li>
            <li>Compliance with TCPA and CTIA guidelines</li>
            <li>Proper message frequency disclosure</li>
          </ul>
        </div>

        <div style={{ background: 'linear-gradient(135deg, #1e40af 0%, #3b82f6 100%)', color: 'white', padding: '3rem', borderRadius: '12px', textAlign: 'center' }}>
          <h2 style={{ fontSize: '2rem', fontWeight: '700', marginBottom: '1rem' }}>
            Ready to Get Started?
          </h2>
          <p style={{ fontSize: '1.1rem', marginBottom: '2rem', opacity: 0.95 }}>
            Start managing your leads and customers more effectively today
          </p>
          <Link to="/contact" style={{
            display: 'inline-block',
            background: '#fbbf24',
            color: '#1f2937',
            padding: '1rem 2.5rem',
            borderRadius: '8px',
            textDecoration: 'none',
            fontWeight: '600',
            fontSize: '1.1rem'
          }}>
            Start Free Trial
          </Link>
        </div>
      </div>
    </>
  );
}
